import { Routes } from '@angular/router';
import { HomeLayoutComponent } from './components/layouts/home-layout/home-layout.components';
import { SimpleLayoutComponent } from './components/layouts/single-layout/single-layout.components';
import { HomeComponent } from './components/home/home.component';
import { ScrapPricingComponent } from './components/scrap-pricing/scrap-pricing.component';
import { CustomersComponent } from './components/customers/customers.component';
import { CreateCustomerComponent } from './components/customers/create-customer/create-customer.component';
import { SubscriptionsComponent } from './components/subscriptions/subscriptions.component';
import { CreateSubscriptionComponent } from './components/subscriptions/create-subscription/create-subscription.component';
import { AgentsComponent } from './components/agents/agents.component';
import { AgentBusinessReportComponent } from './components/agents/agent-business-report/agent-business-report.component';
import { AgentSelfieCheckinsComponent } from './components/agents/agent-selfie-checkins/agent-selfie-checkins.component';
import { PlatformChargeTransactionsComponent } from './components/platform-charge-transactions/platform-charge-transactions.component';
import { UnsupportedGeoLocationsComponent } from './components/unsupported-geo-locations/unsupported-geo-locations.component';
import { UnsupportedGeoLocationsListComponent } from './components/unsupported-geo-locations/unsupported-geo-locations-list.component';
import { ScrapCollectionDashboardComponent } from './components/scrap-collection-dashboard/scrap-collection-dashboard.component';
import { GeoZoneManagerComponent } from './components/geo-zone-manager/geo-zone-manager.component';
import { MarketingLeadsComponent } from './components/marketing/leads/marketing-leads.component';
import { MarketingPlaceCollectorComponent } from './components/marketing/place-collector/marketing-place-collector.component';
import { WhatsAppCareQueueComponent } from './components/marketing/whatsapp-care-queue/whatsapp-care-queue.component';
import { CampaignsComponent } from './components/marketing/campaigns/campaigns.component';
import { MarketingReportsComponent } from './components/marketing/reports/reports.component';
import { MarketingAcquisitionComponent } from './components/marketing/acquisition/acquisition.component';
import { WhatsAppSendLogComponent } from './components/marketing/whatsapp-send-log/whatsapp-send-log.component';
import { ReportsComponent } from './components/reports/reports.component';
import { AgentInsightsReportComponent } from './components/reports/agent-insights-report/agent-insights-report.component';
import { CustomerCollectionsReportComponent } from './components/reports/customer-collections-report/customer-collections-report.component';
import { AppointmentsListComponent } from './components/appointments-list/appointments-list.component';
import { AppointmentDetailComponent } from './components/appointments-list/appointment-detail/appointment-detail.component';
import { BulkScrapRequestsListComponent } from './components/bulk-scrap-requests/bulk-scrap-requests-list.component';
import { ScrapCollectionsListComponent } from './components/scrap-collections-list/scrap-collections-list.component';
import { ScrapCollectionDetailComponent } from './components/scrap-collections-list/scrap-collection-detail/scrap-collection-detail.component';
import { AppointmentCreateComponent } from './components/appointment-create/appointment-create.component';
import { CumulativeChartsComponent } from './components/charts/cumulative-charts/cumulative-charts.component';
import { DailyGlanceComponent } from './components/daily-glance/daily-glance.component';
import { NonScrapTransactionsComponent } from './components/non-scrap-transactions/non-scrap-transactions.component';
import { RecurringSchedulesComponent } from './components/recurring-schedules/recurring-schedules.component';
import { VouchersComponent } from './components/vouchers/vouchers.component';
import { PartnersComponent } from './components/partners/partners.component';
import { PartnerInvoicesComponent } from './components/partners/partner-invoices/partner-invoices.component';
import { VoucherConfigurationComponent } from './components/voucher-configuration/voucher-configuration.component';
import { AgentCommissionsListComponent } from './components/agents/agent-commissions-list/agent-commissions-list.component';
import { ScrapCategoriesComponent } from './components/scrap-categories/scrap-categories.component';
import { DistrictsComponent } from './components/districts/districts.component';
import { AgentInvoicesComponent } from './components/agents/agent-invoices/agent-invoices.component';
import { AgentTransactionsListComponent } from './components/agents/agent-transactions-list/agent-transactions-list.component';
import { AgentLeavesComponent } from './components/agents/agent-leaves/agent-leaves.component';
import { AgentAttendanceCalendarComponent } from './components/agents/agent-attendance-calendar/agent-attendance-calendar.component';
import { AgentQuotasComponent } from './components/agents/agent-quotas/agent-quotas.component';
import { AgentLedgerListComponent } from './components/agents/agent-ledger-list/agent-ledger-list.component';
import { PettyCashListComponent } from './components/agents/petty-cash-list/petty-cash-list.component';
import { AgentLeaderboardComponent } from './components/agents/agent-leaderboard/agent-leaderboard.component';
import { SettingsComponent } from './components/settings/settings.component';
import { HolidaysComponent } from './components/holidays/holidays.component';
import { UnauthorisedComponent } from './components/unauthorised/unauthorised.component';
import { authGuard } from './guards/auth.guard';
import { PermissionGuard } from './guards/permission.guard';

/**
 * RBAC routing strategy:
 *
 *   data.permission = <CC_PERMISSIONS constant>
 *
 * The PermissionGuard reads this value and checks it against the flat permission
 * set returned by GET /api/config/user-permissions.
 *
 * CC Admins have ALL permissions, so they pass every guard automatically.
 * Telemarketers/Marketers only have marketing permissions → blocked from all
 * other routes and redirected to /unauthorised.
 *
 * Nav section → required permission mapping:
 *   Appointments          Appointments_Manage
 *   Management            ScrapItem_Manage (used for Scrap Pricing)
 *   Customers             Customer_Manage
 *   Agents                Agent_Manage
 *   Operations            ScrapCollection_Manage
 *   Geography             GeoZone_Manage
 *   Vouchers              Voucher_Manage
 *   Voucher Config        VoucherConfig_Manage
 *   Partners              Partner_Manage
 *   Partner invoices      Partner_Manage (generate) + Partner_ViewInvoices (grid); route allows either
 *   Analytics/Dashboard   Admin_Manage
 *   Marketing             Marketing_View
 */
export const routes: Routes = [
  // ── Public / Home ──────────────────────────────────────────────────────
  {
    path: '',
    component: HomeLayoutComponent,
    children: [{ path: '', component: HomeComponent }],
  },

  // ── Unauthorised page ──────────────────────────────────────────────────
  {
    path: 'unauthorised',
    component: SimpleLayoutComponent,
    children: [{ path: '', component: UnauthorisedComponent }],
  },


  {
    path: 'appointments-list',
    component: SimpleLayoutComponent,
    canActivate: [authGuard, PermissionGuard],
    data: { permission: ['Appointments_Manage', 'Telemarketer_Permissions'] },
    children: [
      { path: '', component: AppointmentsListComponent },
      { path: 'create/:customerId', component: AppointmentCreateComponent },
      { path: ':id', component: AppointmentDetailComponent },
    ],
  },
  {
    path: 'recurring-schedules',
    component: SimpleLayoutComponent,
    canActivate: [authGuard, PermissionGuard],
    data: { permission: ['Appointments_Manage', 'Telemarketer_Permissions'] },
    children: [{ path: '', component: RecurringSchedulesComponent }],
  },
  {
    // Ops (Appointments_Manage / Telemarketer / CC Admin) can manage; marketing can view.
    path: 'bulk-requests',
    component: SimpleLayoutComponent,
    canActivate: [authGuard, PermissionGuard],
    data: {
      permission: [
        'Appointments_Manage',
        'Telemarketer_Permissions',
        'CCAdmin_Permissions',
        'Marketing_View',
        'Marketer_Permissions',
      ],
    },
    children: [{ path: '', component: BulkScrapRequestsListComponent }],
  },

  // ── Scrap Categories (ScrapItem_Manage) ───────────────────────────────
  {
    path: 'scrap-categories',
    component: SimpleLayoutComponent,
    canActivate: [authGuard, PermissionGuard],
    data: { permission: 'ScrapItem_Manage' },
    children: [{ path: '', component: ScrapCategoriesComponent }],
  },

  // ── Scrap Pricing — CCAdmin + Telemarketer ────────────────────────────
  {
    path: 'scrap-pricing',
    component: SimpleLayoutComponent,
    canActivate: [authGuard, PermissionGuard],
    data: { permission: ['ScrapItem_Manage', 'Telemarketer_Permissions'] },
    children: [{ path: '', component: ScrapPricingComponent }],
  },

  // ── Customers — CCAdmin + Telemarketer (Customer_Manage in composite) ────────
  {
    path: 'customers',
    component: SimpleLayoutComponent,
    canActivate: [authGuard, PermissionGuard],
    data: { permission: ['Customer_Manage', 'Telemarketer_Permissions'] },
    children: [
      { path: '', component: CustomersComponent },
      { path: 'create', component: CreateCustomerComponent },
    ],
  },

  // ── Subscriptions — CCAdmin + Telemarketer (Subscription_Manage in composite) ─
  {
    path: 'subscriptions',
    component: SimpleLayoutComponent,
    canActivate: [authGuard, PermissionGuard],
    data: {
      permission: [
        'Admin_Manage',
        'Subscription_Manage',
        'Telemarketer_Permissions',
      ],
    },
    children: [
      { path: '', component: SubscriptionsComponent },
      { path: 'create', component: CreateSubscriptionComponent },
    ],
  },

  // ── Agents — CCAdmin ONLY (Agent_Manage removed from Telemarketer composite) ──
  {
    path: 'agents',
    component: SimpleLayoutComponent,
    canActivate: [authGuard, PermissionGuard],
    data: { permission: 'Agent_Manage' },
    children: [
      { path: '', component: AgentsComponent },
      { path: 'business-report', component: AgentBusinessReportComponent },
      { path: 'commissions', component: AgentCommissionsListComponent },
      { path: 'selfie-checkins', component: AgentSelfieCheckinsComponent },
      { path: 'invoices', component: AgentInvoicesComponent },
      { path: 'transactions', component: AgentTransactionsListComponent },
      { path: 'leaves', component: AgentLeavesComponent },
      { path: 'quotas', component: AgentQuotasComponent },
      { path: 'ledgers', component: AgentLedgerListComponent },
      { path: 'petty-cash', component: PettyCashListComponent },
      { path: 'leaderboard', component: AgentLeaderboardComponent },
      { path: 'attendance', component: AgentAttendanceCalendarComponent },
      { path: 'attendance/:agentId', component: AgentAttendanceCalendarComponent },
    ],
  },
  {
    path: 'petty-cash',
    component: SimpleLayoutComponent,
    canActivate: [authGuard, PermissionGuard],
    data: { permission: 'Agent_Manage' },
    children: [
      { path: '', component: PettyCashListComponent },
    ],
  },

  // ── Scrap Collections (ScrapCollection_Manage) ─────────────────────────
  {
    path: 'scrap-collections-list',
    component: SimpleLayoutComponent,
    canActivate: [authGuard, PermissionGuard],
    data: { permission: 'ScrapCollection_Manage' },
    children: [
      { path: '', component: ScrapCollectionsListComponent },
      { path: ':id', component: ScrapCollectionDetailComponent },
    ],
  },
  {
    path: 'scrap-collection-insights',
    component: SimpleLayoutComponent,
    canActivate: [authGuard, PermissionGuard],
    data: { permission: 'ScrapCollection_Manage' },
    children: [{ path: '', component: ScrapCollectionDashboardComponent }],
  },
  {
    path: 'daily-glance',
    component: SimpleLayoutComponent,
    canActivate: [authGuard, PermissionGuard],
    data: { permission: ['Report_View', 'CCAdmin_Permissions'] },
    children: [{ path: '', component: DailyGlanceComponent }],
  },

  // ── Platform Charges / Non-Scrap (Admin_Manage) ────────────────────────
  {
    path: 'platform-charge-transactions',
    component: SimpleLayoutComponent,
    canActivate: [authGuard, PermissionGuard],
    data: { permission: 'Admin_Manage' },
    children: [{ path: '', component: PlatformChargeTransactionsComponent }],
  },
  {
    path: 'non-scrap-transactions',
    component: SimpleLayoutComponent,
    canActivate: [authGuard, PermissionGuard],
    data: { permission: 'Admin_Manage' },
    children: [{ path: '', component: NonScrapTransactionsComponent }],
  },
  {
    path: 'settings',
    component: SimpleLayoutComponent,
    canActivate: [authGuard, PermissionGuard],
    data: { permission: 'Admin_Manage' },
    children: [{ path: '', component: SettingsComponent }],
  },
  {
    path: 'holidays',
    component: SimpleLayoutComponent,
    canActivate: [authGuard, PermissionGuard],
    data: {
      permission: [
        'Holiday_Manage',
        'Holiday_List',
        'Holiday_Create',
        'CCAdmin_Permissions',
      ],
    },
    children: [{ path: '', component: HolidaysComponent }],
  },

  // ── Geography — CCAdmin + Telemarketer + Marketer ─────────────────────
  {
    path: 'geo-zones',
    component: SimpleLayoutComponent,
    canActivate: [authGuard, PermissionGuard],
    data: {
      permission: [
        'GeoZone_Manage',
        'Telemarketer_Permissions',
        'Marketer_Permissions',
      ],
    },
    children: [{ path: '', component: GeoZoneManagerComponent }],
  },
  {
    path: 'districts',
    component: SimpleLayoutComponent,
    canActivate: [authGuard, PermissionGuard],
    data: {
      permission: [
        'District_Manage',
        'Telemarketer_Permissions',
        'Marketer_Permissions',
      ],
    },
    children: [{ path: '', component: DistrictsComponent }],
  },
  {
    path: 'unsupported-geo-locations',
    component: SimpleLayoutComponent,
    canActivate: [authGuard, PermissionGuard],
    data: {
      permission: [
        'GeoZone_Manage',
        'Telemarketer_Permissions',
        'Marketer_Permissions',
      ],
    },
    children: [
      { path: '', component: UnsupportedGeoLocationsComponent },
      { path: 'list', component: UnsupportedGeoLocationsListComponent },
    ],
  },

  // ── Analytics (Admin_Manage) ───────────────────────────────────────────
  {
    path: 'analytics',
    component: SimpleLayoutComponent,
    canActivate: [authGuard, PermissionGuard],
    data: { permission: 'Admin_Manage' },
    children: [{ path: '', component: CumulativeChartsComponent }],
  },

  // ── Vouchers (Voucher_Manage) ──────────────────────────────────────────
  {
    path: 'vouchers',
    component: SimpleLayoutComponent,
    canActivate: [authGuard, PermissionGuard],
    data: { permission: 'Voucher_Manage' },
    children: [{ path: '', component: VouchersComponent }],
  },
  {
    path: 'voucher-configuration',
    component: SimpleLayoutComponent,
    canActivate: [authGuard, PermissionGuard],
    data: { permission: 'VoucherConfig_Manage' },
    children: [{ path: '', component: VoucherConfigurationComponent }],
  },

  // ── Partners — list requires Partner_Manage; invoices page allows Manage OR ViewInvoices ──
  {
    path: 'partners',
    component: SimpleLayoutComponent,
    canActivate: [authGuard],
    children: [
      {
        path: '',
        component: PartnersComponent,
        canActivate: [PermissionGuard],
        data: { permission: 'Partner_Manage' },
      },
      {
        path: 'invoices',
        component: PartnerInvoicesComponent,
        canActivate: [PermissionGuard],
        data: {
          permission: [
            'Partner_Manage',
            'Partner_ViewInvoices',
            'CCAdmin_Permissions',
          ],
        },
      },
    ],
  },

  // ── Marketing (Marketing_View) — Marketer, Telemarketer, CCAdmin ────────
  // Array used because backend returns either the granular permission
  // (Marketing_View) OR a composite sentinel (Marketer_Permissions /
  // Telemarketer_Permissions) depending on rbac.config.json.
  {
    path: 'marketing',
    component: SimpleLayoutComponent,
    canActivate: [authGuard, PermissionGuard],
    data: {
      permission: [
        'Marketing_View', // when backend expands composites
        'Marketer_Permissions', // CS-Marketer sentinel
        'Telemarketer_Permissions', // CS-TeleMarketer sentinel
        'CCAdmin_Permissions', // CC Admin always has access
      ],
    },
    children: [
      { path: 'leads', component: MarketingLeadsComponent },
      { path: 'place-collector', component: MarketingPlaceCollectorComponent },
      { path: 'care-queue', component: WhatsAppCareQueueComponent },
      { path: 'campaigns', component: CampaignsComponent },
      { path: 'reports', component: MarketingReportsComponent },
      { path: 'acquisition', component: MarketingAcquisitionComponent },
      { path: 'whatsapp-send-log', component: WhatsAppSendLogComponent },
    ],
  },

  // ── Reports (Report_View) — CCAdmin only ────────────────────────────────
  {
    path: 'reports',
    component: SimpleLayoutComponent,
    canActivate: [authGuard, PermissionGuard],
    data: {
      permission: ['Report_View', 'CCAdmin_Permissions'],
    },
    children: [
      { path: '', component: ReportsComponent },
      { path: 'agent-insights', component: AgentInsightsReportComponent },
      { path: 'customer-collections', component: CustomerCollectionsReportComponent },
    ],
  },

  // ── Wildcard ───────────────────────────────────────────────────────────
  { path: '**', redirectTo: '' },
];

